import { Button, ModalWindow } from '@cian/ui-kit';

import { type FC, type SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';

import {
  useCardOfferIsEarlyAccessFlow,
  useCardOfferPublisherContext,
} from '@/microfrontends/listing/shared/context/selectors';
import { formatPhone } from '@/microfrontends/listing/shared/utils/formatPhone';

import { notification } from '@/packages/notifications-center';
import { useListingLayoutGridTypeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { PortalEventStoper } from '@/packages/shared-ui';
import { memoWithDisplayName } from '@/packages/utils';

import { useEarlyAccessFlow } from '../../hooks/card/useEarlyAccessFlow';

import { ContactsModalContent } from './componentns/ContactsModalContent';
import { usePhoneTracking } from './hooks';

type ContactsModalContainerProps = {
  open: boolean;
  onClose(): void;
};

const ContactsModalContainer: FC<ContactsModalContainerProps> = ({ open, onClose }) => {
  const { phones, workHours, name, typeLabels } = useCardOfferPublisherContext();
  const notificationRef = useRef<number | string | undefined>();
  const phoneEntries = useMemo<{ raw: string; formatted: string }[]>(() => {
    return phones.map(phone => ({
      raw: `+${phone.countryCode}${phone.number}`,
      formatted: formatPhone(phone),
    }));
  }, [phones]);

  const onCopy = useCallback((_: string): void => {
    if (notificationRef.current === undefined || !notification.isActive(notificationRef.current)) {
      notificationRef.current = notification.success('Номер телефона скопирован');
    }
  }, []);

  const publisher = useMemo(() => {
    return `${typeLabels?.[0]} ${name}`.trim();
  }, [name, typeLabels]);

  return (
    <ModalWindow maxWidth={500} open={open} onClose={onClose}>
      <ContactsModalContent phones={phoneEntries} publisher={publisher} workHours={workHours} onCopy={onCopy} />
    </ModalWindow>
  );
};

type ContactsButtonProps = {
  onClick(e?: SyntheticEvent): void;
};

const ContactsButton: FC<ContactsButtonProps> = ({ onClick }) => {
  const { trackEarlyAccessConfirmed, trackEarlyAccessBlocked, trackPhoneButtonClick } = usePhoneTracking();

  const [isLoading, setIsLoading] = useState(false);

  const isEarlyAccessFlow = useCardOfferIsEarlyAccessFlow();

  const handleEarlyAccessConfirmed = useCallback(() => {
    trackEarlyAccessConfirmed();
    onClick();
  }, [trackEarlyAccessConfirmed, onClick]);

  const handleEarlyAccessFlow = useEarlyAccessFlow({
    onConfirmed: handleEarlyAccessConfirmed,
    onBlocked: trackEarlyAccessBlocked,
  });

  const gridType = useListingLayoutGridTypeSelector();

  const handleButtonClick = useCallback(
    async (e: SyntheticEvent): Promise<void> => {
      e.stopPropagation();

      if (isEarlyAccessFlow) {
        setIsLoading(true);
        await handleEarlyAccessFlow();
        setIsLoading(false);

        return;
      }

      trackPhoneButtonClick();

      onClick(e);
    },
    [handleEarlyAccessFlow, isEarlyAccessFlow, onClick, trackPhoneButtonClick],
  );

  return (
    <Button
      fullWidth
      data-testid="publisher.phone-button"
      loading={isLoading}
      size={gridType === 'oneColumn' ? 'M' : 'XS'}
      theme="fill_primary"
      onClick={handleButtonClick}
    >
      Позвонить
    </Button>
  );
};

export const ContactsButtonRenderer = memoWithDisplayName<
  Omit<ContactsButtonProps, 'onClick' | 'loading'> & { text?: string }
>(props => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <ContactsButton {...props} onClick={() => setIsOpen(true)} />
      <PortalEventStoper>
        <ContactsModalContainer open={isOpen} onClose={() => setIsOpen(false)} />
      </PortalEventStoper>
    </>
  );
}, 'ContactsButtonRenderer');
